"use client";

import { useState, type FC, type Ref } from "react";
import { mergeClasses, padNumber } from "@huub-web/utils";
import {
  Container,
  type ContainerProps,
  Heading,
  Message,
  ProgressBar,
  SSRWrapper,
  useDelayedState,
  type ClassNameProp,
  Card,
  useIntersectionObserver,
  mergeRefs,
  Text,
} from "@huub-web/ui";

import { Section } from "../Section";
import { SundialSlider } from "./components/SundialSlider";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";

import {
  cardStyle,
  timeControlsOverlayStyle,
  timeControlsContentStyle,
  mapboxContainerStyle,
} from "./SundialAnalysis.css";
import { DynamicSundialMap } from "./components/DynamicSundialMap";
import { AnalysisIndicator } from "../AnalysisIndicator";
import {
  analysisSectionIllustrations,
  type AnalysisSectionIndex,
} from "../Analysis/types/AnalysisSections";
import { isWebGLSupported } from "../MapboxMapView";

export type SundialAnalysisProps = ClassNameProp & {
  /**
   * Index of the section
   **/
  index?: AnalysisSectionIndex;
  /**
   * Address of the location
   **/
  address?: string;
  /**
   * Center coordinates [latitude, longitude]
   **/
  center: [number, number];
  /**
   * Mapbox access token
   **/
  accessToken?: string;
  /**
   * Optional map style URL
   **/
  mapStyle?: string;
  /**
   * Whether sundial features are enabled
   **/
  enabled?: boolean;
  /**
   * Initial time for sun position (default: current time)
   **/
  initialTime?: Date;
  /**
   * Container size
   **/
  container?: ContainerProps["size"];
  /**
   * Show map labels (default: false)
   **/
  showLabels?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * SundialAnalysis component displays a Mapbox map with 3D buildings
 * and dynamic sun shadows that can be controlled by time
 */
export const SundialAnalysis: FC<SundialAnalysisProps> = ({
  index,
  center,
  accessToken,
  enabled = true,
  container = "marketing",
  initialTime = (() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    return today;
  })(),
  className,
  ref,
}) => {
  const internalRef = useContentMenuSection<HTMLDivElement>({
    id: "sundial",
    label: "Zonnestand",
    index,
  });

  const [isMapReady, setIsMapReady] = useDelayedState<boolean>(false, 500);

  const webGLSupported = isWebGLSupported();

  const id = `analysisSection${index}`;

  const [currentTime, setCurrentTime] = useState<Date>(initialTime);
  const [timeOfDay, setTimeOfDay] = useState<number>(initialTime.getHours());

  const { ref: mapRef, isIntersecting: isMapIntersecting } =
    useIntersectionObserver<HTMLDivElement>(undefined, {
      triggerOnce: true,
      threshold: 0.2,
    });

  // Handle time change
  const handleTimeChange = (hour: number, minutes: number) => {
    const newTime = new Date(currentTime);
    newTime.setHours(hour);
    newTime.setMinutes(minutes);

    setTimeOfDay(minutes === 59 ? hour + 1 : hour);
    setCurrentTime(newTime);
  };

  // Handle date change from DateField
  const handleDateChange = (dateString: string) => {
    try {
      const [year, month, day] = dateString.split("-").map(Number);
      const newTime = new Date(
        year,
        month - 1,
        day,
        currentTime.getHours(),
        currentTime.getMinutes(),
      );

      if (Number.isNaN(newTime.getTime())) {
        console.error("Invalid date created from:", dateString);
        return;
      }

      setCurrentTime(newTime);
    } catch (error) {
      console.error("Failed to parse date:", dateString, error);
    }
  };

  // Handle map ready
  const handleMapReady = () => {
    setIsMapReady(true);
  };

  // Check if access token is provided
  if (!accessToken) {
    return (
      <Section id={id}>
        <Container size={container}>
          <Message variant="error" body="Access token is required for MapBox" />
        </Container>
      </Section>
    );
  }

  // Check if sundial features are enabled
  if (!enabled) {
    return (
      <Section id={id}>
        <Container size={container}>
          {index !== undefined && (
            <AnalysisIndicator
              index={index}
              illustration={analysisSectionIllustrations[index]}
            />
          )}
          <Heading
            variant="main"
            title="Zonnestand"
            description="Bekijk de zonnestand en schaduwwerking gedurende de dag voor deze locatie. Gebruik de tijdlijn en datumkiezer om door verschillende momenten te scrollen en zie hoe de zon invloed heeft op de woning en omgeving."
          />
          <Message
            variant="warning"
            body="Zonnestand data is niet beschikbaar voor deze locatie"
          />
        </Container>
      </Section>
    );
  }

  return (
    <Section id={id} ref={mergeRefs(ref, internalRef)}>
      <Container size={container} ref={mapRef}>
        {index !== undefined && (
          <AnalysisIndicator
            index={index}
            illustration={analysisSectionIllustrations[index]}
          />
        )}
        <Heading
          variant="main"
          title="Zonnestand"
          description="Bekijk de zonnestand en schaduwwerking gedurende de dag voor deze locatie. Gebruik de tijdlijn en datumkiezer om door verschillende momenten te scrollen en zie hoe de zon invloed heeft op de woning en omgeving."
        />
        <SSRWrapper>
          {!webGLSupported ? (
            <Message
              variant="warning"
              body="Je browser ondersteunt geen WebGL, wat nodig is voor de schaduwanalyse."
            />
          ) : (
            <Card
              className={mergeClasses(className)}
              innerClassName={cardStyle}
              overflowHidden
              padding="none"
            >
              {!isMapReady && (
                <ProgressBar position="absolute" variant="infinite" />
              )}

              {isMapIntersecting && (
                <div className={mapboxContainerStyle}>
                  <DynamicSundialMap
                    center={center}
                    accessToken={accessToken}
                    onReady={handleMapReady}
                    time={currentTime}
                  />
                  <div className={timeControlsOverlayStyle}>
                    <Container size={container}>
                      <Card
                        variant="blur"
                        contentClassName={timeControlsContentStyle}
                        padding="s"
                      >
                        <SundialSlider
                          value={timeOfDay}
                          location={center}
                          date={currentTime}
                          selectedDate={currentTime}
                          onTimeChange={handleTimeChange}
                          onDateChange={handleDateChange}
                        />
                      </Card>
                    </Container>
                  </div>
                </div>
              )}
            </Card>
          )}
        </SSRWrapper>
      </Container>
      {index !== undefined && (
        <Text align="center">{padNumber(index)} &ndash; Zonnestand</Text>
      )}
    </Section>
  );
};
